import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { Controller, MilkControl } from '@homeleite/domain';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import {
    ApplicationLog,
    FailLogResult,
    LogResult
} from '../../../../../../../../libs/domain/src/lib/logs/application-log';
import { ApplicationLogsRepository } from '../../../../../../../../libs/domain/src/lib/logs/application-logs-repository';
import { EMPTY } from '@homeleite/common-utils';

export interface MilkControlLogGroup {
    milkControl: MilkControl;
    logs: ApplicationLog[];
}

const PAGE_SIZE = 10;

@Component({
    selector: 'homeleite-controller-details-logs',
    templateUrl: './controller-details-logs.component.html',
    styleUrl: './controller-details-logs.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ControllerDetailsLogsComponent implements OnInit, OnDestroy {
    @Input() controller: Controller;

    groupedLogs: MilkControlLogGroup[] = [];
    currentPage = 0;
    isLoading = false;
    hasMoreLogs = true;

    private allLogs: ApplicationLog[] = [];
    private searchSubject = new BehaviorSubject<string>(EMPTY);
    private destroy$ = new Subject<void>();

    constructor(
        private readonly repository: ApplicationLogsRepository,
        private readonly changeDetector: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        if (this.controller) {
            this.fetchLogs(true);

            this.searchSubject.pipe(
                debounceTime(400),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            ).subscribe(() => {
                this.fetchLogs(true);
            });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    fetchLogs(isNewSearch = false): void {
        if (this.isLoading) return;

        if (isNewSearch) {
            this.currentPage = 0;
            this.allLogs = [];
            this.hasMoreLogs = true;
        }

        this.isLoading = true;
        this.repository.pageControllerLogsBy(this.controller.id, this.searchSubject.value, this.currentPage, PAGE_SIZE)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                    this.changeDetector.markForCheck();
                })
            )
            .subscribe({
                next: newLogs => {
                    if (newLogs.length < PAGE_SIZE) {
                        this.hasMoreLogs = false;
                    }

                    this.allLogs.push(...newLogs);
                    this.groupedLogs = this.groupLogsByMilkControl(this.allLogs);
                },
                error: err => {
                    console.error("Erro ao buscar logs do controller:", err);
                    this.hasMoreLogs = false;
                }
            });
    }

    onSearchChange(term: string): void {
        this.searchSubject.next(term);
    }

    loadMore(): void {
        if (this.isLoading || !this.hasMoreLogs) return;
        this.currentPage++;
        this.fetchLogs(false);
    }

    private groupLogsByMilkControl(logs: ApplicationLog[]): MilkControlLogGroup[] {
        const groups = new Map<string, MilkControlLogGroup>();
        for (const log of logs) {
            if (!log.milkControl?.id) continue;
            let group = groups.get(log.milkControl.id);
            if (!group) {
                group = { milkControl: log.milkControl, logs: [] };
                groups.set(log.milkControl.id, group);
            }
            group.logs.push(log);
        }
        return Array.from(groups.values()).sort((a, b) => {
            const dateA = new Date(a.logs[0]?.result?.end || 0).getTime();
            const dateB = new Date(b.logs[0]?.result?.end || 0).getTime();
            return dateB - dateA;
        });
    }
}
